import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useApi } from "./AuthContext";

/**
 * DynamicTranslationContext
 * ---------------------------------------------
 * Handles on-the-fly translation of user-generated content (asset names,
 * addresses, biographies, notes …) that is not present in the static
 * i18next JSON files.
 *
 * Strategy:
 * - When the active language is the source language (fr), no-op (returns
 *   original text immediately).
 * - When the active language differs from the source, strings are batched
 *   and sent to `POST /api/translate` which is backed by Gemini Flash and
 *   caches every translation in MongoDB.
 * - The frontend also maintains an in-memory cache keyed by
 *   `${lang}::${originalText}` so successive renders never re-fetch.
 * - The `useDynamicT(text)` hook returns the translated text and triggers a
 *   background fetch if it hasn't been fetched yet.
 */

const DTContext = createContext(null);

const SOURCE_LANG = "fr";
const BATCH_INTERVAL_MS = 120;
const MAX_BATCH_SIZE = 40;

export const DynamicTranslationProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const api = useApi();
  const lang = i18n.language?.startsWith("en") ? "en" : "fr";

  // { "en::Some FR text": "Some EN text" }
  const [cache, setCache] = useState({});
  const cacheRef = useRef(cache);
  cacheRef.current = cache;

  // Pending fetch queue
  const pendingRef = useRef(new Set());
  const inFlightRef = useRef(new Set());
  const timerRef = useRef(null);

  const flush = useCallback(async () => {
    timerRef.current = null;
    if (lang === SOURCE_LANG) return;
    const pending = Array.from(pendingRef.current).filter(
      (t) => !inFlightRef.current.has(`${lang}::${t}`) && !cacheRef.current[`${lang}::${t}`]
    );
    if (pending.length === 0) return;
    const batch = pending.slice(0, MAX_BATCH_SIZE);
    batch.forEach((t) => {
      pendingRef.current.delete(t);
      inFlightRef.current.add(`${lang}::${t}`);
    });
    try {
      const { data } = await api.post("/translate", {
        texts: batch,
        target: lang,
        source: SOURCE_LANG,
      });
      const translations = data?.translations || [];
      setCache((prev) => {
        const next = { ...prev };
        batch.forEach((orig, i) => {
          next[`${lang}::${orig}`] = translations[i] ?? orig;
        });
        return next;
      });
    } catch (e) {
      // On failure, cache the original so we don't re-loop indefinitely
      setCache((prev) => {
        const next = { ...prev };
        batch.forEach((orig) => {
          next[`${lang}::${orig}`] = orig;
        });
        return next;
      });
    } finally {
      batch.forEach((t) => inFlightRef.current.delete(`${lang}::${t}`));
      // Drain remaining pending in another batch
      if (pendingRef.current.size > 0) {
        timerRef.current = setTimeout(flush, BATCH_INTERVAL_MS);
      }
    }
  }, [api, lang]);

  const request = useCallback(
    (text) => {
      if (!text || typeof text !== "string") return;
      if (lang === SOURCE_LANG) return;
      if (cacheRef.current[`${lang}::${text}`]) return;
      if (inFlightRef.current.has(`${lang}::${text}`)) return;
      pendingRef.current.add(text);
      if (!timerRef.current) {
        timerRef.current = setTimeout(flush, BATCH_INTERVAL_MS);
      }
    },
    [lang, flush]
  );

  // Reset cache-effect flow when language changes: we don't wipe the cache
  // (translations remain valid), we just re-trigger requests for missing entries.
  useEffect(() => {
    pendingRef.current.clear();
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, [lang]);

  return (
    <DTContext.Provider value={{ lang, cache, request }}>
      {children}
    </DTContext.Provider>
  );
};

/**
 * useDynamicT(text)
 * Returns the translated string synchronously if cached, else the original
 * text (while a background fetch populates the cache).
 */
export const useDynamicT = (text) => {
  const ctx = useContext(DTContext);
  useEffect(() => {
    if (ctx && text) ctx.request(text);
  }, [ctx, text]);
  if (!ctx || !text || typeof text !== "string") return text;
  if (ctx.lang === SOURCE_LANG) return text;
  return ctx.cache[`${ctx.lang}::${text}`] || text;
};

/**
 * <T>{someUserText}</T>
 * Convenience wrapper for JSX inline usage. Accepts a single string child.
 */
export const T = ({ children }) => {
  const translated = useDynamicT(typeof children === "string" ? children : null);
  if (typeof children !== "string") return children;
  return translated;
};

export const useCurrentLang = () => {
  const ctx = useContext(DTContext);
  return ctx?.lang || "fr";
};
